import React from "react";
import "./MouldPlate.css";

/*
 * MouldPlate - the signature artifact.
 *
 * A technical drawing of a six-tooth anterior arch with its dimensions
 * called out, drawn the way a spec sheet would draw it.
 *
 * Why this and not a decorative shape: the product's entire job is finding a
 * mould by its measurements, so a drawing of a measured mould is the one
 * image that could not be lifted onto any other site. The ring it replaces
 * was a 2rem band of rgba(147, 130, 255, 0.35) - a purple blob, which is the
 * single most recognisable machine-made decoration there is, and it said
 * nothing about teeth.
 *
 * Everything is stroked in currentColor at varying opacity, so the plate
 * inherits the page's ink rather than introducing a colour.
 */

/* The arch the crowns are seated on. */
const RX = 165;
const RY = 36;
const CX = 260;
const CY = 150;

const at = (th) => ({
  x: CX + RX * Math.sin(th),
  y: CY + RY * Math.cos(th),
});

/*
 * Crowns are placed by ARC LENGTH, not by picking an angle per tooth.
 *
 * Picking angles is what makes a drawn arch look wrong: an ellipse covers
 * different distances per degree depending where you are on it, so evenly
 * spaced angles leave the front teeth crowded and the canines stranded with
 * gaps. Walking the curve and spending each tooth's own width makes the row
 * touch the way a real arch does, at any RX/RY.
 */
const STEP = 0.0015;
const CURVE = [{ th: 0, s: 0 }];
for (let th = STEP, s = 0; th < 1.6; th += STEP) {
  const a = at(th - STEP);
  const b = at(th);
  s += Math.hypot(b.x - a.x, b.y - a.y);
  CURVE.push({ th, s });
}
const thetaAt = (s) => (CURVE.find((e) => e.s >= s) || CURVE[CURVE.length - 1]).th;

/* One quadrant, midline outward: central incisor, lateral, canine. Widths
   hold roughly the proportions of a real anterior set - the central is the
   widest, the lateral the narrowest, the canine the longest. */
const QUADRANT = [
  { w: 58, h: 70, label: "1" },
  { w: 48, h: 60, label: "2" },
  { w: 50, h: 68, label: "3" },
];

/* Walk outward from the midline, spending each crown's width as we go. */
const SEATS = [];
let run = 0;
QUADRANT.forEach((tooth) => {
  const centreS = run + tooth.w / 2;
  const th = thetaAt(centreS);
  const { x, y } = at(th);

  /*
   * A crown stands perpendicular to the arch, so its long axis follows the
   * curve's outward normal rather than staying upright. Rotating by the raw
   * angle (the obvious thing to do) turns the crowns the wrong way and the
   * row reads as a broken fan.
   *
   * For an SVG rotation R, the crown's local +y axis lands on
   * (-sin R, cos R). Setting that parallel to the outward normal
   * (RY sin th, RX cos th) gives R = atan2(-RY sin th, RX cos th).
   */
  const rot = (Math.atan2(-RY * Math.sin(th), RX * Math.cos(th)) * 180) / Math.PI;

  SEATS.push({ ...tooth, x, y, rot, mirrored: false });
  SEATS.push({ ...tooth, x: 2 * CX - x, y, rot: -rot, mirrored: true });
  run += tooth.w;
});

const SPAN = run; // half the arch, in drawing units
const OUTER = at(thetaAt(SPAN));

/*
 * The height dimension measures the outermost crown - the canine - and its
 * geometry is DERIVED from that crown's own seat rather than typed in. A
 * hardcoded pair of y values drifts out of alignment the moment any crown
 * size or arch radius above changes, and a dimension line that does not
 * touch what it claims to measure is the tell that the drawing is a prop.
 *
 * For an SVG rotate(R), a local point (0, +/-h/2) lands at
 * (-/+ (h/2) sin R, +/- (h/2) cos R) from the crown's centre.
 */
const CANINE = SEATS.filter((t) => !t.mirrored).slice(-1)[0];
const CANINE_R = (CANINE.rot * Math.PI) / 180;
const CERVICAL_Y = CANINE.y - (CANINE.h / 2) * Math.cos(CANINE_R);
const INCISAL_Y = CANINE.y + (CANINE.h / 2) * Math.cos(CANINE_R);
const CERVICAL_X = CANINE.x + (CANINE.h / 2) * Math.sin(CANINE_R);
const INCISAL_X = CANINE.x - (CANINE.h / 2) * Math.sin(CANINE_R);

/* A crown outline: square at the cervical edge, tapering slightly and
   rounding at the incisal edge, which is the direction a real anterior
   tooth actually narrows. */
function crownPath(w, h) {
  const hw = w / 2;
  const iw = hw * 0.88;
  const r = 6;
  return [
    `M ${-hw} ${-h / 2}`,
    `L ${hw} ${-h / 2}`,
    `L ${iw} ${h / 2 - r}`,
    `Q ${iw} ${h / 2} ${iw - r} ${h / 2}`,
    `L ${-iw + r} ${h / 2}`,
    `Q ${-iw} ${h / 2} ${-iw} ${h / 2 - r}`,
    "Z",
  ].join(" ");
}

/*
 * The arch guide, offset OUTWARD along the curve's normal rather than run
 * through the crown centres. Through the centres it is a correct
 * construction line that is also completely invisible, because the crowns
 * are filled and painted over it - so it costs a path and shows nothing.
 * Offset past the incisal edges it does what a construction line is for:
 * it shows the curve the crowns were seated on.
 *
 * It is drawn only across the span the crowns occupy. A construction line
 * stops where the thing it constructs stops.
 */
const GUIDE_OFFSET = 48; // clears the tallest crown's half-height (35)
const GUIDE = (() => {
  const pts = [];
  for (let s = -SPAN; s <= SPAN; s += 4) {
    const th = thetaAt(Math.abs(s));
    const p = at(th);
    /* Outward unit normal on the ellipse at th */
    const nx = RY * Math.sin(th);
    const ny = RX * Math.cos(th);
    const len = Math.hypot(nx, ny) || 1;
    const ox = (nx / len) * GUIDE_OFFSET;
    const oy = (ny / len) * GUIDE_OFFSET;
    const x = s < 0 ? 2 * CX - (p.x + ox) : p.x + ox;
    pts.push(`${x.toFixed(1)} ${(p.y + oy).toFixed(1)}`);
  }
  return `M ${pts.join(" L ")}`;
})();

const DIM_Y = 258; // width dimension
const RULE_Y = 300; // ruled baseline
const HDIM_X = 468; // height dimension
const L = CX - SPAN;
const R = CX + SPAN;

/* Ruler ticks along the baseline: a major tick every fifth, minor between.
   The same rhythm the texture library uses, so the plate and the page
   surfaces share one drawing language. */
const TICKS = Array.from({ length: 41 }, (_, i) => i);

export default function MouldPlate({ className = "" }) {
  return (
    <svg
      className={`mould-plate ${className}`.trim()}
      viewBox="0 0 520 360"
      /* The caliper's travel is handed to CSS rather than hardcoded in the
         keyframes, so it always stops at the ruler's real ends even if the
         crown sizes or arch radius above change. */
      style={{ "--mp-travel-from": `${L}px`, "--mp-travel-to": `${R}px` }}
      role="img"
      aria-label="Technical drawing of a six-tooth anterior mould, with its width and height dimensions marked"
    >
      {/* Corner registration marks. Four, sparing - the plate reads as a
          drawing on a sheet, without laying graph paper under the page. */}
      {[
        [18, 18, 1, 1],
        [502, 18, -1, 1],
        [18, 342, 1, -1],
        [502, 342, -1, -1],
      ].map(([x, y, sx, sy]) => (
        <path
          key={`${x}-${y}`}
          d={`M ${x} ${y + 15 * sy} L ${x} ${y} L ${x + 15 * sx} ${y}`}
          className="mp-crop"
        />
      ))}

      <path d={GUIDE} className="mp-guide" />
      <line x1={CX} y1={CY - 34} x2={CX} y2={CY + RY + 96} className="mp-guide" />

      {SEATS.map(({ x, y, rot, w, h, label }, i) => (
        <g key={i} transform={`translate(${x} ${y}) rotate(${rot})`}>
          <path d={crownPath(w, h)} className="mp-crown" />
          {/* The incisal third, marked the way a shade map marks it */}
          <line
            x1={-w * 0.38}
            y1={h * 0.17}
            x2={w * 0.38}
            y2={h * 0.17}
            className="mp-crown-detail"
          />
          <text y={4} className="mp-crown-label">
            {label}
          </text>
        </g>
      ))}

      {/* Width dimension across the full arch. Tick terminators rather than
          filled arrowheads, which is what a drawing actually uses. */}
      <g className="mp-dim">
        <line x1={L} y1={DIM_Y} x2={R} y2={DIM_Y} />
        <line x1={L} y1={DIM_Y - 6} x2={L} y2={DIM_Y + 6} />
        <line x1={R} y1={DIM_Y - 6} x2={R} y2={DIM_Y + 6} />
        {/* Extension lines back up to what is being measured */}
        <line x1={L} y1={OUTER.y} x2={L} y2={DIM_Y - 8} className="mp-ext" />
        <line x1={R} y1={OUTER.y} x2={R} y2={DIM_Y - 8} className="mp-ext" />
      </g>
      <text x={CX} y={DIM_Y - 10} className="mp-dim-label" textAnchor="middle">
        6 ANTERIOR WIDTH
      </text>

      {/* Height dimension on the right, its extension lines running from the
          canine's own cervical and incisal edges so it is attached to the
          crown it measures instead of floating beside it. */}
      <g className="mp-dim">
        <line x1={HDIM_X} y1={CERVICAL_Y} x2={HDIM_X} y2={INCISAL_Y} />
        <line x1={HDIM_X - 6} y1={CERVICAL_Y} x2={HDIM_X + 6} y2={CERVICAL_Y} />
        <line x1={HDIM_X - 6} y1={INCISAL_Y} x2={HDIM_X + 6} y2={INCISAL_Y} />
        <line
          x1={CERVICAL_X}
          y1={CERVICAL_Y}
          x2={HDIM_X - 8}
          y2={CERVICAL_Y}
          className="mp-ext"
        />
        <line
          x1={INCISAL_X}
          y1={INCISAL_Y}
          x2={HDIM_X - 8}
          y2={INCISAL_Y}
          className="mp-ext"
        />
      </g>
      <text
        x={488}
        y={(CERVICAL_Y + INCISAL_Y) / 2}
        className="mp-dim-label"
        transform={`rotate(-90 488 ${(CERVICAL_Y + INCISAL_Y) / 2})`}
        textAnchor="middle"
      >
        HEIGHT
      </text>

      {/* Ruled baseline, spanning exactly the width the dimension above it
          measures. A ruler running to a different width than the dimension
          it sits under is decoration wearing a measurement's clothes. */}
      <g className="mp-rule">
        <line x1={L} y1={RULE_Y} x2={R} y2={RULE_Y} />
        {TICKS.map((i) => {
          const x = L + (i * (R - L)) / (TICKS.length - 1);
          const major = i % 5 === 0;
          return (
            <line
              key={i}
              x1={x}
              y1={RULE_Y}
              x2={x}
              y2={RULE_Y + (major ? 11 : 6)}
              opacity={major ? 0.55 : 0.28}
            />
          );
        })}
      </g>

      {/*
        The one moving part: a caliper mark that travels the baseline.
        It animates transform only, it carries no content, and every label
        and line above is fully drawn without it - so if the animation never
        runs, nothing is missing from the drawing.
      */}
      <g className="mp-caliper" aria-hidden="true">
        <line x1={0} y1={RULE_Y - 16} x2={0} y2={RULE_Y + 14} />
        <path
          d={`M -4 ${RULE_Y - 16} L 4 ${RULE_Y - 16} L 0 ${RULE_Y - 9} Z`}
        />
      </g>
    </svg>
  );
}
